import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import api, { getApiErrorMessage } from '../../services/api';
import { 
  SystemSettings, 
  SystemSettingsUpdate, 
  Industry, 
  JobPositionWithIndustry, 
  SmokingPreventionMeasure,
  FAQ
} from '../../types';

// コンポーネント用の型定義
interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
  title, 
  children, 
  defaultExpanded = false 
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Paper sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="h6" component="h2">
          {title}
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 2, pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

interface SystemSettingsTabProps {
  storeId: string;
}

const SystemSettingsTab: React.FC<SystemSettingsTabProps> = ({ storeId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // システム設定管理用状態
  const [settings, setSettings] = useState<SystemSettings[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState<SystemSettings | null>(null);
  const [formData, setFormData] = useState<SystemSettingsUpdate>({
    setting_value: '',
    setting_type: '',
    category: '',
    description: '',
    display_name: '',
    display_order: 0,
    is_active: true
  });

  // FAQ管理用状態
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [faqDetailDialogOpen, setFaqDetailDialogOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<FAQ | null>(null);
  const [faqFormData, setFaqFormData] = useState({
    question: '',
    answer: '',
    display_order: 0,
    is_active: true
  });
  const [isNewFaq, setIsNewFaq] = useState(false);

  // 業種管理用状態
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [industryDialogOpen, setIndustryDialogOpen] = useState(false);
  const [industryDetailDialogOpen, setIndustryDetailDialogOpen] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);
  const [industryFormData, setIndustryFormData] = useState({
    name: '',
    description: '',
    display_order: 0,
    is_active: true
  });
  const [isNewIndustry, setIsNewIndustry] = useState(false);

  // 職種管理用状態
  const [jobPositions, setJobPositions] = useState<JobPositionWithIndustry[]>([]);
  const [jobPositionDialogOpen, setJobPositionDialogOpen] = useState(false);
  const [jobPositionDetailDialogOpen, setJobPositionDetailDialogOpen] = useState(false);
  const [selectedJobPosition, setSelectedJobPosition] = useState<JobPositionWithIndustry | null>(null);
  const [jobPositionFormData, setJobPositionFormData] = useState({
    name: '',
    industry_id: '',
    display_order: 0,
    is_active: true
  });
  const [isNewJobPosition, setIsNewJobPosition] = useState(false);
  // 職種削除確認ダイアログ用
  const [jobPositionDeleteDialogOpen, setJobPositionDeleteDialogOpen] = useState(false);
  const [jobPositionToDelete, setJobPositionToDelete] = useState<JobPositionWithIndustry | null>(null);

  // 受動喫煙防止措置管理用状態
  const [smokingMeasures, setSmokingMeasures] = useState<SmokingPreventionMeasure[]>([]);
  const [smokingMeasureDialogOpen, setSmokingMeasureDialogOpen] = useState(false);
  const [smokingMeasureDetailDialogOpen, setSmokingMeasureDetailDialogOpen] = useState(false);
  const [selectedSmokingMeasure, setSelectedSmokingMeasure] = useState<SmokingPreventionMeasure | null>(null);
  const [smokingMeasureFormData, setSmokingMeasureFormData] = useState({
    code: '',
    name: '',
    description: '',
    display_order: 0,
    is_active: true
  });
  const [isNewSmokingMeasure, setIsNewSmokingMeasure] = useState(false);

  // 共通状態
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // 設定タイプ選択肢
  const settingTypes = [
    { value: 'string', label: '文字列' },
    { value: 'number', label: '数値' },
    { value: 'boolean', label: '真偽値' },
    { value: 'json', label: 'JSON' },
    { value: 'url', label: 'URL' }
  ];

  // カテゴリ選択肢
  const categories = [
    { value: 'general', label: '一般' },
    { value: 'url', label: 'URL' },
    { value: 'email', label: 'メール' },
    { value: 'notification', label: '通知' },
    { value: 'maintenance', label: 'メンテナンス' }
  ];

  // 初期データ取得
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // システム設定を取得
        const settingsData = await api.systemSettings.getAllSettings();
        setSettings(settingsData);
        
        // FAQを取得
        const faqsData = await api.faq.getAllForAdmin();
        setFaqs(faqsData);
        
        // 業種を取得
        const industriesData = await api.industry.getAll();
        setIndustries(industriesData);
        
        // 受動喫煙防止措置を取得
        const smokingData = await api.smokingPrevention.getAll();
        setSmokingMeasures(smokingData.measures);
        
        // 職種を取得（storeIdが必要）
        if (storeId) {
          const jobPositionsData = await api.jobPosition.getAllWithIndustry(storeId);
          setJobPositions(jobPositionsData);
        }
        
        setError(null);
      } catch (err: any) {
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [storeId]);

  // システム設定関連の処理
  const handleViewDetails = (setting: SystemSettings) => {
    setSelectedSetting(setting);
    setDetailDialogOpen(true);
  };

  const handleEditClick = async (setting: SystemSettings) => {
    try {
      const latestSetting = await api.systemSettings.getById(setting.id);
      setSelectedSetting(latestSetting);
      
      setFormData({
        setting_value: latestSetting.setting_value || '',
        setting_type: latestSetting.setting_type || '',
        category: latestSetting.category || '',
        description: latestSetting.description || '',
        display_name: latestSetting.display_name || '',
        display_order: latestSetting.display_order || 0,
        is_active: latestSetting.is_active
      });
      
      setSubmitAttempted(false);
      setError(null);
      setEditDialogOpen(true);
    } catch (err: any) {
      setError('設定データの取得に失敗しました');
    }
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    
    if (!isFormValid()) {
      return;
    }

    if (!selectedSetting || !selectedSetting.id) {
      setError('設定IDが取得できません');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await api.systemSettings.updateSetting(selectedSetting.id, formData);
      handleCloseEditDialog();
      
      // データを再取得
      const data = await api.systemSettings.getAllSettings();
      setSettings(data);
    } catch (err: any) {
      setError('保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  // FAQ関連の処理
  const handleFaqAdd = () => {
    setIsNewFaq(true);
    setSelectedFaq(null);
    setFaqFormData({
      question: '',
      answer: '',
      display_order: 0,
      is_active: true
    });
    setSubmitAttempted(false);
    setError(null);
    setFaqDialogOpen(true);
  };

  const handleFaqEdit = (faq: FAQ) => {
    setIsNewFaq(false);
    setSelectedFaq(faq);
    setFaqFormData({
      question: faq.question,
      answer: faq.answer,
      display_order: faq.display_order,
      is_active: faq.is_active
    });
    setSubmitAttempted(false);
    setError(null);
    setFaqDialogOpen(true);
  };

  const handleFaqView = (faq: FAQ) => {
    setSelectedFaq(faq);
    setFaqDetailDialogOpen(true);
  };

  const handleFaqSubmit = async () => {
    setSubmitAttempted(true);

    if (!faqFormData.question.trim() || !faqFormData.answer.trim()) {
      return;
    }

    if (!isNewFaq && !selectedFaq) {
      setError('FAQが選択されていません');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (isNewFaq) {
        await api.faq.create(faqFormData);
      } else if (selectedFaq) {
        await api.faq.update(selectedFaq.id, faqFormData);
      }
      setFaqDialogOpen(false);

      // データを再取得
      const data = await api.faq.getAllForAdmin();
      setFaqs(data);
    } catch (err: any) {
      setError('FAQの保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  // 業種関連の処理
  const handleIndustryAdd = () => {
    setIsNewIndustry(true);
    setSelectedIndustry(null);
    setIndustryFormData({
      name: '',
      description: '',
      display_order: 0,
      is_active: true
    });
    setSubmitAttempted(false);
    setError(null);
    setIndustryDialogOpen(true);
  };

  const handleIndustryEdit = (industry: Industry) => {
    setIsNewIndustry(false);
    setSelectedIndustry(industry);
    setIndustryFormData({
      name: industry.name,
      description: industry.description || '',
      display_order: industry.display_order,
      is_active: industry.is_active
    });
    setSubmitAttempted(false);
    setError(null);
    setIndustryDialogOpen(true);
  };

  const handleIndustryView = (industry: Industry) => {
    setSelectedIndustry(industry);
    setIndustryDetailDialogOpen(true);
  };

  const handleIndustrySubmit = async () => {
    setSubmitAttempted(true);
    
    if (!industryFormData.name.trim()) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (isNewIndustry) {
        await api.industry.create(industryFormData);
      } else if (selectedIndustry) {
        await api.industry.update(selectedIndustry.id, industryFormData);
      }
      setIndustryDialogOpen(false);
      
      // データを再取得
      const data = await api.industry.getAll();
      setIndustries(data);
    } catch (err: any) {
      setError('業種の保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  // 職種関連の処理
  // per-store化: 自店の職種のみ編集・削除可能（store_id が自店＝storeId のもの）。
  // 全店共有(store_id NULL)や他店舗の職種は読み取り専用。
  const canModifyJobPosition = (jobPosition: JobPositionWithIndustry) =>
    !!jobPosition.store_id && jobPosition.store_id === storeId;

  const handleJobPositionAdd = () => {
    setIsNewJobPosition(true);
    setSelectedJobPosition(null);
    setJobPositionFormData({
      name: '',
      industry_id: '',
      display_order: 0,
      is_active: true
    });
    setSubmitAttempted(false);
    setError(null);
    setJobPositionDialogOpen(true);
  };

  const handleJobPositionEdit = (jobPosition: JobPositionWithIndustry) => {
    setIsNewJobPosition(false);
    setSelectedJobPosition(jobPosition);
    setJobPositionFormData({
      name: jobPosition.name,
      industry_id: jobPosition.industry_id || '',
      display_order: jobPosition.display_order ?? 0,
      is_active: jobPosition.is_active
    });
    setSubmitAttempted(false);
    setError(null);
    setJobPositionDialogOpen(true);
  };

  const handleJobPositionView = (jobPosition: JobPositionWithIndustry) => {
    setSelectedJobPosition(jobPosition);
    setJobPositionDetailDialogOpen(true);
  };

  const handleJobPositionSubmit = async () => {
    setSubmitAttempted(true);

    if (!jobPositionFormData.name.trim()) {
      return;
    }

    if (!storeId) {
      setError('店舗IDが取得できません');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (isNewJobPosition) {
        await api.jobPosition.create({
          store_id: storeId,
          ...jobPositionFormData
        });
      } else if (selectedJobPosition) {
        await api.jobPosition.update(selectedJobPosition.id, jobPositionFormData, storeId);
      }
      setJobPositionDialogOpen(false);

      // データを再取得
      const data = await api.jobPosition.getAllWithIndustry(storeId);
      setJobPositions(data);
    } catch (err: any) {
      // バックエンドの実エラー内容（重複・制約など）を表示して原因を分かるようにする。
      // getApiErrorMessage が 5xx / 内部情報を含む文言をフォールバックに差し替える。
      setError(getApiErrorMessage(err, '職種の保存に失敗しました'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleJobPositionDeleteClick = (jobPosition: JobPositionWithIndustry) => {
    setJobPositionToDelete(jobPosition);
    setError(null);
    setJobPositionDeleteDialogOpen(true);
  };

  const handleJobPositionDeleteConfirm = async () => {
    if (!jobPositionToDelete || !storeId) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await api.jobPosition.delete(jobPositionToDelete.id, storeId);
      setJobPositionDeleteDialogOpen(false);
      setJobPositionToDelete(null);

      // データを再取得
      const data = await api.jobPosition.getAllWithIndustry(storeId);
      setJobPositions(data);
    } catch (err: any) {
      // 求人ひな形で使用中などのエラー内容を表示
      setError(getApiErrorMessage(err, '職種の削除に失敗しました'));
    } finally {
      setIsSaving(false);
    }
  };

  // 受動喫煙防止措置関連の処理
  const handleSmokingMeasureAdd = () => {
    setIsNewSmokingMeasure(true);
    setSelectedSmokingMeasure(null);
    setSmokingMeasureFormData({
      code: '',
      name: '',
      description: '',
      display_order: 0,
      is_active: true
    });
    setSubmitAttempted(false);
    setError(null);
    setSmokingMeasureDialogOpen(true);
  };

  const handleSmokingMeasureEdit = (smokingMeasure: SmokingPreventionMeasure) => {
    setIsNewSmokingMeasure(false);
    setSelectedSmokingMeasure(smokingMeasure);
    setSmokingMeasureFormData({
      code: smokingMeasure.code,
      name: smokingMeasure.name,
      description: smokingMeasure.description || '',
      display_order: smokingMeasure.display_order,
      is_active: smokingMeasure.is_active
    });
    setSubmitAttempted(false);
    setError(null);
    setSmokingMeasureDialogOpen(true);
  };

  const handleSmokingMeasureView = (smokingMeasure: SmokingPreventionMeasure) => {
    setSelectedSmokingMeasure(smokingMeasure);
    setSmokingMeasureDetailDialogOpen(true);
  };

  const handleSmokingMeasureSubmit = async () => {
    setSubmitAttempted(true);
    
    if (!smokingMeasureFormData.code.trim() || !smokingMeasureFormData.name.trim()) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (isNewSmokingMeasure) {
        await api.smokingPrevention.create(smokingMeasureFormData);
      } else if (selectedSmokingMeasure) {
        await api.smokingPrevention.update(selectedSmokingMeasure.id, smokingMeasureFormData);
      }
      setSmokingMeasureDialogOpen(false);
      
      // データを再取得
      const data = await api.smokingPrevention.getAll();
      setSmokingMeasures(data.measures);
    } catch (err: any) {
      setError('受動喫煙防止措置の保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  // ダイアログを閉じる処理
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedSetting(null);
    setFormData({
      setting_value: '',
      setting_type: '',
      category: '',
      description: '',
      display_name: '',
      display_order: 0,
      is_active: true
    });
    setSubmitAttempted(false);
    setError(null);
  };

  // フォーム入力変更
  const handleInputChange = (field: keyof SystemSettingsUpdate) => 
    (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent | React.SyntheticEvent) => {
      let value: string | number | boolean;
      
      if (field === 'is_active') {
        value = (event.target as HTMLInputElement).checked;
      } else if (event.target && 'value' in event.target) {
        value = event.target.value;
        
        if (field === 'display_order' && typeof value === 'string') {
          value = parseInt(value, 10) || 0;
        }
      } else {
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    };

  // バリデーション
  const isFormValid = () => {
    return !!formData.setting_value && 
           !!formData.setting_type && 
           !!formData.category && 
           (formData.display_order !== undefined && formData.display_order >= 0);
  };

  // テーブル用のレンダリング関数
  const renderDataTable = (
    data: any[],
    columns: { key: string; label: string; render?: (item: any) => React.ReactNode }[],
    onView: (item: any) => void,
    onEdit: (item: any) => void,
    onDelete?: (item: any) => void,
    canModify: (item: any) => boolean = () => true
  ) => {
    if (isMobile) {
      return (
        <Box>
          {data.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body1" color="text.secondary">
                データがありません
              </Typography>
            </Box>
          ) : (
            data.map((item, index) => (
              <Card key={item.id || index} sx={{ mb: 2 }}>
                <CardContent>
                  {columns.map(column => (
                    <Box key={column.key} sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {column.label}
                      </Typography>
                      <Typography variant="body2">
                        {column.render ? column.render(item) : item[column.key]}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => onView(item)}
                    >
                      詳細
                    </Button>
                    {canModify(item) && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => onEdit(item)}
                        color="primary"
                      >
                        編集
                      </Button>
                    )}
                    {onDelete && canModify(item) && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => onDelete(item)}
                        color="error"
                      >
                        削除
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      );
    }

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell key={column.key}>{column.label}</TableCell>
                ))}
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <Typography color="text.secondary">
                      データがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item, index) => (
                  <TableRow key={item.id || index} hover>
                    {columns.map(column => (
                      <TableCell key={column.key}>
                        {column.render ? column.render(item) : item[column.key]}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => onView(item)}
                        color="info"
                        sx={{ mr: 1 }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {canModify(item) && (
                        <IconButton
                          size="small"
                          onClick={() => onEdit(item)}
                          color="primary"
                          sx={{ mr: onDelete ? 1 : 0 }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {onDelete && canModify(item) && (
                        <IconButton
                          size="small"
                          onClick={() => onDelete(item)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  // 職種一覧の列定義（説明・必要な条件・給与範囲は不要のため非表示。表示順を追加）
  const jobPositionColumns = [
    {
      key: 'name',
      label: '職種名',
      render: (item: JobPositionWithIndustry) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{item.name}</span>
          {!item.store_id && (
            <Chip label="共有" size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    { key: 'display_order', label: '表示順' },
    {
      key: 'is_active',
      label: '状態',
      render: (item: JobPositionWithIndustry) => (
        <Chip
          label={item.is_active ? '有効' : '無効'}
          color={item.is_active ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h5" gutterBottom>
            システム共通設定
          </Typography>
          <Typography variant="body1" color="text.secondary">
            システム全体の設定を管理します。
          </Typography>
        </div>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* システム設定セクション */}
          <CollapsibleSection title="システム設定" defaultExpanded={true}>
            {renderDataTable(
              settings,
              [
                { key: 'setting_key', label: '設定キー' },
                { key: 'display_name', label: '表示名', render: (item) => item.display_name || '-' },
                { 
                  key: 'setting_value', 
                  label: '設定値', 
                  render: (item) => (
                    <Box sx={{ 
                      maxWidth: '250px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.setting_value}
                    </Box>
                  )
                },
                { key: 'display_order', label: '表示順' },
                { 
                  key: 'is_active', 
                  label: '状態', 
                  render: (item) => (
                    <Chip 
                      label={item.is_active ? "有効" : "無効"} 
                      color={item.is_active ? "success" : "default"}
                      size="small"
                    />
                  )
                }
              ],
              handleViewDetails,
              handleEditClick
            )}
          </CollapsibleSection>

          {/* 業種管理セクション */}
          <CollapsibleSection title="業種管理">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleIndustryAdd}
              >
                業種追加
              </Button>
            </Box>
            {renderDataTable(
              industries,
              [
                { key: 'name', label: '業種名' },
                { key: 'description', label: '説明', render: (item) => item.description || '-' },
                { key: 'display_order', label: '表示順' },
                { 
                  key: 'is_active', 
                  label: '状態', 
                  render: (item) => (
                    <Chip 
                      label={item.is_active ? "有効" : "無効"} 
                      color={item.is_active ? "success" : "default"}
                      size="small"
                    />
                  )
                }
              ],
              handleIndustryView,
              handleIndustryEdit
            )}
          </CollapsibleSection>

          {/* 職種管理セクション */}
          <CollapsibleSection title="職種管理">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleJobPositionAdd}
              >
                職種追加
              </Button>
            </Box>
            {jobPositions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  データがありません
                </Typography>
              </Box>
            ) : (
              <>
                {[...industries]
                  .sort((a, b) => a.display_order - b.display_order)
                  .map(industry => {
                    const grouped = jobPositions
                      .filter(jp => jp.industry_id === industry.id)
                      .sort((a, b) => (a.display_order - b.display_order) || a.name.localeCompare(b.name, 'ja'));
                    if (grouped.length === 0) return null;
                    return (
                      <Box key={industry.id} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                          {industry.name}
                        </Typography>
                        {renderDataTable(
                          grouped,
                          jobPositionColumns,
                          handleJobPositionView,
                          handleJobPositionEdit,
                          handleJobPositionDeleteClick,
                          canModifyJobPosition
                        )}
                      </Box>
                    );
                  })}
                {(() => {
                  const noIndustry = jobPositions
                    .filter(jp => !jp.industry_id)
                    .sort((a, b) => (a.display_order - b.display_order) || a.name.localeCompare(b.name, 'ja'));
                  if (noIndustry.length === 0) return null;
                  return (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        業種なし
                      </Typography>
                      {renderDataTable(
                        noIndustry,
                        jobPositionColumns,
                        handleJobPositionView,
                        handleJobPositionEdit,
                        handleJobPositionDeleteClick,
                        canModifyJobPosition
                      )}
                    </Box>
                  );
                })()}
              </>
            )}
          </CollapsibleSection>

          {/* 受動喫煙防止措置管理セクション */}
          <CollapsibleSection title="受動喫煙防止措置管理">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleSmokingMeasureAdd}
              >
                受動喫煙防止措置追加
              </Button>
            </Box>
            {renderDataTable(
              smokingMeasures,
              [
                { key: 'code', label: 'コード' },
                { key: 'name', label: '名称' },
                { key: 'description', label: '説明', render: (item) => item.description || '-' },
                { key: 'display_order', label: '表示順' },
                { 
                  key: 'is_active', 
                  label: '状態', 
                  render: (item) => (
                    <Chip 
                      label={item.is_active ? "有効" : "無効"} 
                      color={item.is_active ? "success" : "default"}
                      size="small"
                    />
                  )
                }
              ],
              handleSmokingMeasureView,
              handleSmokingMeasureEdit
            )}
          </CollapsibleSection>

          {/* FAQ管理セクション - 受動喫煙防止措置管理の後に配置 */}
          <CollapsibleSection title="FAQ管理">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleFaqAdd}
              >
                FAQ追加
              </Button>
            </Box>
            {renderDataTable(
              faqs,
              [
                { 
                  key: 'question', 
                  label: '質問', 
                  render: (item) => (
                    <Box sx={{ 
                      maxWidth: '300px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.question}
                    </Box>
                  )
                },
                { 
                  key: 'answer', 
                  label: '回答', 
                  render: (item) => (
                    <Box sx={{ 
                      maxWidth: '250px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.answer}
                    </Box>
                  )
                },
                { key: 'display_order', label: '表示順' },
                { 
                  key: 'is_active', 
                  label: '状態', 
                  render: (item) => (
                    <Chip 
                      label={item.is_active ? "有効" : "無効"} 
                      color={item.is_active ? "success" : "default"}
                      size="small"
                    />
                  )
                }
              ],
              handleFaqView,
              handleFaqEdit
            )}
          </CollapsibleSection>
        </>
      )}

      {/* システム設定詳細表示ダイアログ */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          システム設定詳細
          <IconButton
            aria-label="close"
            onClick={() => setDetailDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSetting && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  設定キー
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSetting.setting_key}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  表示名
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSetting.display_name || '-'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  設定タイプ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSetting.setting_type}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  設定値
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all', mb: 2 }}>
                  {selectedSetting.setting_value}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  カテゴリ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSetting.category}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  説明
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSetting.description || '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  表示順
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSetting.display_order}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  状態
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSetting.is_active ? '有効' : '無効'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  作成日時
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSetting.created_at ? new Date(selectedSetting.created_at).toLocaleString('ja-JP') : '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  最終更新日時
                </Typography>
                <Typography variant="body1">
                  {selectedSetting.updated_at ? new Date(selectedSetting.updated_at).toLocaleString('ja-JP') : '-'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDetailDialogOpen(false)}
            color="primary"
          >
            閉じる
          </Button>
          <Button
            onClick={() => {
              setDetailDialogOpen(false);
              if (selectedSetting) {
                handleEditClick(selectedSetting);
              }
            }}
            variant="contained"
            color="primary"
          >
            編集
          </Button>
        </DialogActions>
      </Dialog>

      {/* システム設定編集モーダル */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          システム設定編集
          <IconButton
            aria-label="close"
            onClick={handleCloseEditDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {selectedSetting && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    設定キー
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedSetting.setting_key}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="表示名"
                  value={formData.display_name}
                  onChange={handleInputChange('display_name')}
                  margin="normal"
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel id="setting-type-label">設定タイプ</InputLabel>
                  <Select
                    labelId="setting-type-label"
                    value={formData.setting_type || ''}
                    label="設定タイプ"
                    onChange={handleInputChange('setting_type')}
                    required
                    error={submitAttempted && !formData.setting_type}
                  >
                    {settingTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="設定値"
                  value={formData.setting_value}
                  onChange={handleInputChange('setting_value')}
                  margin="normal"
                  required
                  error={submitAttempted && !formData.setting_value}
                  helperText={submitAttempted && !formData.setting_value ? '設定値は必須です' : ''}
                  multiline={formData.setting_type === 'json'}
                  rows={formData.setting_type === 'json' ? 4 : 1}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel id="category-label">カテゴリ</InputLabel>
                  <Select
                    labelId="category-label"
                    value={formData.category}
                    label="カテゴリ"
                    onChange={handleInputChange('category')}
                    required
                    error={submitAttempted && !formData.category}
                  >
                    {categories.map(category => (
                      <MenuItem key={category.value} value={category.value}>{category.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="説明"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  margin="normal"
                  multiline
                  rows={2}
                />

                <TextField
                  fullWidth
                  label="表示順"
                  type="number"
                  value={formData.display_order === undefined ? '' : formData.display_order}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = value === '' ? undefined : parseInt(value, 10);
                    setFormData(prev => ({
                      ...prev,
                      display_order: numValue
                    }));
                  }}
                  margin="normal"
                  required
                  error={submitAttempted && (formData.display_order === undefined || formData.display_order < 0)}
                  helperText={submitAttempted && (formData.display_order === undefined || formData.display_order < 0) ? '表示順は0以上の数値で入力してください' : ''}
                  InputProps={{
                    inputProps: { 
                      min: 0,
                      inputMode: 'numeric',
                    }
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.is_active}
                      onChange={handleInputChange('is_active')}
                      color="primary"
                    />
                  }
                  label="有効/無効"
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Button 
            onClick={handleCloseEditDialog}
            color="inherit"
          >
            キャンセル
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid() || isSaving}
          >
            {isSaving ? '保存中...' : '更新'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAQ詳細表示ダイアログ */}
      <Dialog
        open={faqDetailDialogOpen}
        onClose={() => setFaqDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          FAQ詳細
          <IconButton
            aria-label="close"
            onClick={() => setFaqDetailDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedFaq && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  質問
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedFaq.question}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  回答
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  {selectedFaq.answer}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  アイコン名
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedFaq.icon_name}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  アクションボタン
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedFaq.has_action ? 'あり' : 'なし'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  表示順
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedFaq.display_order}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  状態
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedFaq.is_active ? '有効' : '無効'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  作成日時
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(selectedFaq.created_at).toLocaleString('ja-JP')}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  最終更新日時
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedFaq.updated_at).toLocaleString('ja-JP')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setFaqDetailDialogOpen(false)}
            color="primary"
          >
            閉じる
          </Button>
          <Button
            onClick={() => {
              setFaqDetailDialogOpen(false);
              if (selectedFaq) {
                handleFaqEdit(selectedFaq);
              }
            }}
            variant="contained"
            color="primary"
          >
            編集
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAQ編集・新規追加ダイアログ */}
      <Dialog
        open={faqDialogOpen}
        onClose={() => setFaqDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          {isNewFaq ? 'FAQ追加' : 'FAQ編集'}
          <IconButton
            aria-label="close"
            onClick={() => setFaqDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            {!isNewFaq && selectedFaq && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  アイコン名とアクションボタンの設定は変更できません。
                </Alert>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    アイコン名
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedFaq.icon_name}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    アクションボタン
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedFaq.has_action ? 'あり' : 'なし'}
                  </Typography>
                </Box>
              </>
            )}

            <TextField
              fullWidth
              label="質問"
              value={faqFormData.question}
              onChange={(e) => setFaqFormData(prev => ({ ...prev, question: e.target.value }))}
              margin="normal"
              required
              error={submitAttempted && !faqFormData.question.trim()}
              helperText={submitAttempted && !faqFormData.question.trim() ? '質問は必須です' : ''}
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="回答"
              value={faqFormData.answer}
              onChange={(e) => setFaqFormData(prev => ({ ...prev, answer: e.target.value }))}
              margin="normal"
              required
              error={submitAttempted && !faqFormData.answer.trim()}
              helperText={submitAttempted && !faqFormData.answer.trim() ? '回答は必須です' : ''}
              multiline
              rows={4}
            />

            <TextField
              fullWidth
              label="表示順"
              type="number"
              value={faqFormData.display_order}
              onChange={(e) => setFaqFormData(prev => ({ ...prev, display_order: parseInt(e.target.value, 10) || 0 }))}
              margin="normal"
              InputProps={{
                inputProps: { min: 0 }
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={faqFormData.is_active}
                  onChange={(e) => setFaqFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  color="primary"
                />
              }
              label="有効/無効"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaqDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleFaqSubmit}
            variant="contained"
            disabled={!faqFormData.question.trim() || !faqFormData.answer.trim() || isSaving}
          >
            {isSaving ? '保存中...' : (isNewFaq ? '追加' : '更新')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 業種詳細表示ダイアログ */}
      <Dialog
        open={industryDetailDialogOpen}
        onClose={() => setIndustryDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          業種詳細
          <IconButton
            aria-label="close"
            onClick={() => setIndustryDetailDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedIndustry && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  業種名
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedIndustry.name}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  説明
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedIndustry.description || '-'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  表示順
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedIndustry.display_order}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  状態
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedIndustry.is_active ? '有効' : '無効'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  作成日時
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(selectedIndustry.created_at).toLocaleString('ja-JP')}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  最終更新日時
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedIndustry.updated_at).toLocaleString('ja-JP')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIndustryDetailDialogOpen(false)}
            color="primary"
          >
            閉じる
          </Button>
          <Button
            onClick={() => {
              setIndustryDetailDialogOpen(false);
              if (selectedIndustry) {
                handleIndustryEdit(selectedIndustry);
              }
            }}
            variant="contained"
            color="primary"
          >
            編集
          </Button>
        </DialogActions>
      </Dialog>

      {/* 職種詳細表示ダイアログ */}
      <Dialog
        open={jobPositionDetailDialogOpen}
        onClose={() => setJobPositionDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          職種詳細
          <IconButton
            aria-label="close"
            onClick={() => setJobPositionDetailDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedJobPosition && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  職種名
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedJobPosition.name}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  業種
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedJobPosition.industry?.name || '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  管理区分
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedJobPosition.store_id ? '自店舗' : '全店共有'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  表示順
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedJobPosition.display_order}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  状態
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedJobPosition.is_active ? '有効' : '無効'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  作成日時
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(selectedJobPosition.created_at).toLocaleString('ja-JP')}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  最終更新日時
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedJobPosition.updated_at).toLocaleString('ja-JP')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setJobPositionDetailDialogOpen(false)}
            color="primary"
          >
            閉じる
          </Button>
          {selectedJobPosition && canModifyJobPosition(selectedJobPosition) && (
            <Button
              onClick={() => {
                setJobPositionDetailDialogOpen(false);
                if (selectedJobPosition) {
                  handleJobPositionEdit(selectedJobPosition);
                }
              }}
              variant="contained"
              color="primary"
            >
              編集
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 受動喫煙防止措置詳細表示ダイアログ */}
      <Dialog
        open={smokingMeasureDetailDialogOpen}
        onClose={() => setSmokingMeasureDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          受動喫煙防止措置詳細
          <IconButton
            aria-label="close"
            onClick={() => setSmokingMeasureDetailDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSmokingMeasure && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  コード
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSmokingMeasure.code}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  名称
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSmokingMeasure.name}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  説明
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSmokingMeasure.description || '-'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  表示順
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSmokingMeasure.display_order}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  状態
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedSmokingMeasure.is_active ? '有効' : '無効'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  作成日時
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(selectedSmokingMeasure.created_at).toLocaleString('ja-JP')}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  最終更新日時
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedSmokingMeasure.updated_at).toLocaleString('ja-JP')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSmokingMeasureDetailDialogOpen(false)}
            color="primary"
          >
            閉じる
          </Button>
          <Button
            onClick={() => {
              setSmokingMeasureDetailDialogOpen(false);
              if (selectedSmokingMeasure) {
                handleSmokingMeasureEdit(selectedSmokingMeasure);
              }
            }}
            variant="contained"
            color="primary"
          >
            編集
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 業種編集/追加ダイアログ */}
      <Dialog
        open={industryDialogOpen}
        onClose={() => setIndustryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          {isNewIndustry ? '業種追加' : '業種編集'}
          <IconButton
            aria-label="close"
            onClick={() => setIndustryDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="業種名"
            value={industryFormData.name}
            onChange={(e) => setIndustryFormData(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
            required
            error={submitAttempted && !industryFormData.name.trim()}
            helperText={submitAttempted && !industryFormData.name.trim() ? '業種名は必須です' : ''}
          />

          <TextField
            fullWidth
            label="説明"
            value={industryFormData.description}
            onChange={(e) => setIndustryFormData(prev => ({ ...prev, description: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />

          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={industryFormData.display_order}
            onChange={(e) => setIndustryFormData(prev => ({ ...prev, display_order: parseInt(e.target.value, 10) || 0 }))}
            margin="normal"
            InputProps={{
              inputProps: { min: 0 }
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={industryFormData.is_active}
                onChange={(e) => setIndustryFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                color="primary"
              />
            }
            label="有効/無効"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIndustryDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleIndustrySubmit}
            variant="contained"
            disabled={!industryFormData.name.trim() || isSaving}
          >
            {isSaving ? '保存中...' : (isNewIndustry ? '追加' : '更新')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 職種編集/追加ダイアログ */}
      <Dialog
        open={jobPositionDialogOpen}
        onClose={() => setJobPositionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          {isNewJobPosition ? '職種追加' : '職種編集'}
          <IconButton
            aria-label="close"
            onClick={() => setJobPositionDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="職種名"
            value={jobPositionFormData.name}
            onChange={(e) => setJobPositionFormData(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
            required
            error={submitAttempted && !jobPositionFormData.name.trim()}
            helperText={submitAttempted && !jobPositionFormData.name.trim() ? '職種名は必須です' : ''}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="industry-select-label">業種</InputLabel>
            <Select
              labelId="industry-select-label"
              value={jobPositionFormData.industry_id}
              label="業種"
              onChange={(e) => setJobPositionFormData(prev => ({ ...prev, industry_id: e.target.value }))}
            >
              <MenuItem value="">なし</MenuItem>
              {industries.filter(industry => industry.is_active).map(industry => (
                <MenuItem key={industry.id} value={industry.id}>{industry.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={jobPositionFormData.display_order}
            onChange={(e) => setJobPositionFormData(prev => ({ ...prev, display_order: parseInt(e.target.value, 10) || 0 }))}
            margin="normal"
            InputProps={{
              inputProps: { min: 0 }
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={jobPositionFormData.is_active}
                onChange={(e) => setJobPositionFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                color="primary"
              />
            }
            label="有効/無効"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobPositionDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleJobPositionSubmit}
            variant="contained"
            disabled={!jobPositionFormData.name.trim() || isSaving}
          >
            {isSaving ? '保存中...' : (isNewJobPosition ? '追加' : '更新')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 職種削除確認ダイアログ */}
      <Dialog
        open={jobPositionDeleteDialogOpen}
        onClose={() => setJobPositionDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>職種の削除</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body1">
            職種「{jobPositionToDelete?.name}」を削除します。よろしいですか？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            ※ この職種を使用している求人ひな形がある場合は削除できません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobPositionDeleteDialogOpen(false)} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={handleJobPositionDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isSaving}
          >
            {isSaving ? '削除中...' : '削除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 受動喫煙防止措置編集/追加ダイアログ */}
      <Dialog
        open={smokingMeasureDialogOpen}
        onClose={() => setSmokingMeasureDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          {isNewSmokingMeasure ? '受動喫煙防止措置追加' : '受動喫煙防止措置編集'}
          <IconButton
            aria-label="close"
            onClick={() => setSmokingMeasureDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="コード"
            value={smokingMeasureFormData.code}
            onChange={(e) => setSmokingMeasureFormData(prev => ({ ...prev, code: e.target.value }))}
            margin="normal"
            required
            error={submitAttempted && !smokingMeasureFormData.code.trim()}
            helperText={submitAttempted && !smokingMeasureFormData.code.trim() ? 'コードは必須です' : ''}
          />

          <TextField
            fullWidth
            label="名称"
            value={smokingMeasureFormData.name}
            onChange={(e) => setSmokingMeasureFormData(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
            required
            error={submitAttempted && !smokingMeasureFormData.name.trim()}
            helperText={submitAttempted && !smokingMeasureFormData.name.trim() ? '名称は必須です' : ''}
          />

          <TextField
            fullWidth
            label="説明"
            value={smokingMeasureFormData.description}
            onChange={(e) => setSmokingMeasureFormData(prev => ({ ...prev, description: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />

          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={smokingMeasureFormData.display_order}
            onChange={(e) => setSmokingMeasureFormData(prev => ({ ...prev, display_order: parseInt(e.target.value, 10) || 0 }))}
            margin="normal"
            InputProps={{
              inputProps: { min: 0 }
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={smokingMeasureFormData.is_active}
                onChange={(e) => setSmokingMeasureFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                color="primary"
              />
            }
            label="有効/無効"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSmokingMeasureDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleSmokingMeasureSubmit}
            variant="contained"
            disabled={!smokingMeasureFormData.code.trim() || !smokingMeasureFormData.name.trim() || isSaving}
          >
            {isSaving ? '保存中...' : (isNewSmokingMeasure ? '追加' : '更新')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SystemSettingsTab;